import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from "@tabler/icons-react";
import {
  useEffect,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

type ReceivablesPaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  summary: ReactNode;
  isFetching?: boolean;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
};

const NAV_BUTTON_CLASS =
  "inline-flex items-center gap-1 px-2.5 py-1.5 text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent";

function clampPage(value: number, totalPages: number): number {
  return Math.min(Math.max(1, value), Math.max(1, totalPages));
}

function buildPageWindow(page: number, totalPages: number): number[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages, page, page - 1, page + 1]);
  return [...pages]
    .filter((value) => value >= 1 && value <= totalPages)
    .sort((a, b) => a - b);
}

export function ReceivablesPagination({
  page,
  totalPages,
  onPageChange,
  summary,
  isFetching = false,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}: ReceivablesPaginationProps) {
  const [pageInput, setPageInput] = useState(String(page));
  const safeTotalPages = Math.max(1, totalPages);
  const showCompactPageNumbers = safeTotalPages <= 7;

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const goToPage = () => {
    const parsed = Number.parseInt(pageInput.trim(), 10);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(page));
      return;
    }
    const next = clampPage(parsed, safeTotalPages);
    onPageChange(next);
    setPageInput(String(next));
  };

  const handlePageInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      goToPage();
    }
  };

  const handlePageInputBlur = () => {
    const parsed = Number.parseInt(pageInput.trim(), 10);
    if (!Number.isFinite(parsed) || parsed === page) {
      setPageInput(String(page));
      return;
    }
    goToPage();
  };

  const pageWindow = buildPageWindow(page, safeTotalPages);

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm text-slate-500">
      <span>{summary}</span>
      <div className="inline-flex flex-wrap items-center gap-3">
        {pageSize != null && pageSizeOptions && onPageSizeChange ? (
          <label className="inline-flex items-center gap-2 text-slate-600">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(event) =>
                onPageSizeChange(Number.parseInt(event.target.value, 10))
              }
              disabled={isFetching}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-slate-800 disabled:cursor-not-allowed disabled:text-slate-300"
              aria-label="Rows per page"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <nav
          aria-label="Table pagination"
          className="inline-flex items-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
        >
          <button
            type="button"
            disabled={page <= 1 || isFetching}
            onClick={() => onPageChange(1)}
            className={NAV_BUTTON_CLASS}
            aria-label="First page"
            title="First page"
          >
            <IconChevronsLeft size={16} stroke={1.75} aria-hidden />
          </button>
          <button
            type="button"
            disabled={page <= 1 || isFetching}
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            className={`${NAV_BUTTON_CLASS} border-l border-slate-200`}
            aria-label="Previous page"
          >
            <IconChevronLeft size={16} stroke={1.75} aria-hidden />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {showCompactPageNumbers ? (
            <div className="flex items-center border-l border-slate-200">
              {pageWindow.map((pageNumber, index) => {
                const previous = pageWindow[index - 1];
                const showEllipsis = previous != null && pageNumber - previous > 1;

                return (
                  <span key={pageNumber} className="inline-flex items-center">
                    {showEllipsis ? (
                      <span
                        className="px-2 py-1.5 text-slate-400"
                        aria-hidden
                      >
                        …
                      </span>
                    ) : null}
                    <button
                      type="button"
                      disabled={isFetching}
                      onClick={() => onPageChange(pageNumber)}
                      aria-current={pageNumber === page ? "page" : undefined}
                      className={`min-w-9 px-2.5 py-1.5 tabular-nums transition-colors disabled:cursor-not-allowed ${
                        pageNumber === page
                          ? "bg-blue-50 font-semibold text-blue-700"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  </span>
                );
              })}
            </div>
          ) : (
            <label className="inline-flex items-center gap-1.5 border-l border-r border-slate-200 px-3 py-1.5 text-slate-600">
              <span>Page</span>
              <input
                type="number"
                min={1}
                max={safeTotalPages}
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onKeyDown={handlePageInputKeyDown}
                onBlur={handlePageInputBlur}
                disabled={isFetching}
                className="w-12 rounded-md border border-slate-200 px-1.5 py-0.5 text-center text-slate-800 tabular-nums disabled:cursor-not-allowed disabled:text-slate-300"
                aria-label={`Page number, 1 to ${safeTotalPages}`}
              />
              <span>of {safeTotalPages}</span>
            </label>
          )}

          <button
            type="button"
            disabled={page >= safeTotalPages || isFetching}
            onClick={() => onPageChange(Math.min(page + 1, safeTotalPages))}
            className={`${NAV_BUTTON_CLASS} border-l border-slate-200`}
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <IconChevronRight size={16} stroke={1.75} aria-hidden />
          </button>
          <button
            type="button"
            disabled={page >= safeTotalPages || isFetching}
            onClick={() => onPageChange(safeTotalPages)}
            className={`${NAV_BUTTON_CLASS} border-l border-slate-200`}
            aria-label="Last page"
            title="Last page"
          >
            <IconChevronsRight size={16} stroke={1.75} aria-hidden />
          </button>
        </nav>
      </div>
    </div>
  );
}
